import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const ImportantDatesPage = () => {
  const [selectedSeason, setSelectedSeason] = useState('all');

  const importantDates = [
    // Fall Sports
    {
      date: '2024-07-15',
      title: 'Fall Sports Registration Opens',
      description: 'Online registration opens for football, soccer, and cross country',
      category: 'fall',
      type: 'registration',
      deadline: true
    },
    {
      date: '2024-08-01',
      title: 'Fall Sports Physical Deadline',
      description: 'All physicals and required forms must be submitted',
      category: 'fall',
      type: 'deadline',
      deadline: true
    },
    {
      date: '2024-08-05',
      title: 'Fall Sports Parent Meeting',
      description: 'Mandatory meeting for all fall sports parents - 6:00 PM in Auditorium',
      category: 'fall',
      type: 'meeting',
      deadline: false
    },
    {
      date: '2024-08-12',
      title: 'Fall Sports Practices Begin',
      description: 'First day of practice for all fall sports teams',
      category: 'fall',
      type: 'practice',
      deadline: false
    },
    {
      date: '2024-08-26',
      title: 'Fall Sports Season Begins',
      description: 'First games/competitions for fall sports',
      category: 'fall',
      type: 'season',
      deadline: false
    },
    
    // Winter Sports
    {
      date: '2024-10-15',
      title: 'Winter Sports Registration Opens',
      description: 'Online registration opens for basketball and wrestling',
      category: 'winter',
      type: 'registration',
      deadline: true
    },
    {
      date: '2024-11-01',
      title: 'Winter Sports Physical Deadline',
      description: 'All physicals and required forms must be submitted',
      category: 'winter',
      type: 'deadline',
      deadline: true
    },
    {
      date: '2024-11-05',
      title: 'Winter Sports Parent Meeting',
      description: 'Mandatory meeting for all winter sports parents - 6:00 PM in Auditorium',
      category: 'winter',
      type: 'meeting',
      deadline: false
    },
    {
      date: '2024-11-12',
      title: 'Winter Sports Practices Begin',
      description: 'First day of practice for all winter sports teams',
      category: 'winter',
      type: 'practice',
      deadline: false
    },
    {
      date: '2024-12-02',
      title: 'Winter Sports Season Begins',
      description: 'First games/competitions for winter sports',
      category: 'winter',
      type: 'season',
      deadline: false
    },
    
    // Spring Sports
    {
      date: '2025-01-15',
      title: 'Spring Sports Registration Opens',
      description: 'Online registration opens for baseball, softball, track & field, and tennis',
      category: 'spring',
      type: 'registration',
      deadline: true
    },
    {
      date: '2025-02-15',
      title: 'Spring Sports Physical Deadline',
      description: 'All physicals and required forms must be submitted',
      category: 'spring',
      type: 'deadline',
      deadline: true
    },
    {
      date: '2025-02-20',
      title: 'Spring Sports Parent Meeting',
      description: 'Mandatory meeting for all spring sports parents - 6:00 PM in Auditorium',
      category: 'spring',
      type: 'meeting',
      deadline: false
    },
    {
      date: '2025-03-03',
      title: 'Spring Sports Practices Begin',
      description: 'First day of practice for all spring sports teams',
      category: 'spring',
      type: 'practice',
      deadline: false
    },
    {
      date: '2025-03-17',
      title: 'Spring Sports Season Begins',
      description: 'First games/competitions for spring sports',
      category: 'spring',
      type: 'season',
      deadline: false
    },
    
    // General Dates
    {
      date: '2024-09-15',
      title: 'Athletic Awards Banquet',
      description: 'Annual celebration of athletic achievements - 6:00 PM in Cafeteria',
      category: 'general',
      type: 'event',
      deadline: false
    },
    {
      date: '2024-12-15',
      title: 'Winter Athletic Awards',
      description: 'Winter sports recognition ceremony - 6:00 PM in Auditorium',
      category: 'general',
      type: 'event',
      deadline: false
    },
    {
      date: '2025-05-15',
      title: 'Spring Athletic Awards',
      description: 'Spring sports recognition ceremony - 6:00 PM in Auditorium',
      category: 'general',
      type: 'event',
      deadline: false
    }
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'registration': return 'bg-blue-100 text-blue-800';
      case 'deadline': return 'bg-red-100 text-red-800';
      case 'meeting': return 'bg-purple-100 text-purple-800';
      case 'practice': return 'bg-green-100 text-green-800';
      case 'season': return 'bg-orange-100 text-orange-800';
      case 'event': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'registration': return DocumentTextIcon;
      case 'deadline': return ExclamationTriangleIcon;
      case 'meeting': return InformationCircleIcon;
      case 'practice': return ClockIcon;
      case 'season': return CalendarIcon;
      case 'event': return CalendarIcon;
      default: return CalendarIcon;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredDates = selectedSeason === 'all' 
    ? importantDates 
    : importantDates.filter(date => date.category === selectedSeason);

  const sortedDates = filteredDates.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">Important Dates</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Important Dates
          </h1>
          <p className="text-xl text-blue-100">
            Key dates for registration, tryouts, and athletic seasons.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Mark Your Calendar
              </h3>
              <p className="text-yellow-700">
                Please note all important deadlines and dates. Missing registration or physical deadlines 
                may result in delayed participation or ineligibility for the season.
              </p>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Athletic Calendar
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Season:
              </label>
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Seasons</option>
                <option value="fall">Fall Sports</option>
                <option value="winter">Winter Sports</option>
                <option value="spring">Spring Sports</option>
                <option value="general">General Events</option>
              </select>
            </div>
          </div>

          {/* Calendar Events */}
          <div className="space-y-4">
            {sortedDates.map((event, index) => {
              const IconComponent = getTypeIcon(event.type);
              const isUpcoming = new Date(event.date) > new Date();
              
              return (
                <div 
                  key={index} 
                  className={`border rounded-lg p-6 transition-all duration-200 ${
                    isUpcoming ? 'border-primary bg-blue-50' : 'border-gray-200 bg-white'
                  } ${event.deadline ? 'border-l-4 border-l-red-500' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-2 rounded-lg ${isUpcoming ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className={`text-lg font-semibold ${
                            isUpcoming ? 'text-primary' : 'text-gray-900'
                          }`}>
                            {event.title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(event.type)}`}>
                            {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                          </span>
                          {event.deadline && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                              Deadline
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 mb-2">
                          {event.description}
                        </p>
                        <p className={`text-sm font-medium ${
                          isUpcoming ? 'text-primary' : 'text-gray-500'
                        }`}>
                          {formatDate(event.date)}
                        </p>
                      </div>
                    </div>
                    {isUpcoming && (
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Upcoming
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Quick Reference Guide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 p-4 rounded-lg mb-4">
                <DocumentTextIcon className="w-8 h-8 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Registration
              </h3>
              <p className="text-gray-600 text-sm">
                Opens 4-6 weeks before season starts. Complete online registration and submit all required forms.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-red-100 p-4 rounded-lg mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Physical Deadline
              </h3>
              <p className="text-gray-600 text-sm">
                Sports physical must be completed within 12 months and submitted before first practice.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 p-4 rounded-lg mb-4">
                <ClockIcon className="w-8 h-8 text-green-600 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Practice Start
              </h3>
              <p className="text-gray-600 text-sm">
                Practices begin 2-3 weeks before first competition. Attendance is mandatory.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportantDatesPage;